import { Phone } from 'iconoir-react'
import { ReactElement } from 'react'
import Spinner from 'app/components/Spinner'
import SimpleTable from 'app/components/table/SimpleTable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { hueTint } from 'lib/chartColors'
import { SectionTitle } from './SectionTitle'
import type { UseVoiceAgentOpsReturn } from './useVoiceAgentOps'

const REASON_BAR_COLOR = hueTint(`orange`, 0.7)

type NoBookingReasonsSectionProps = Pick<
  UseVoiceAgentOpsReturn,
  | `unbookedCallCount`
  | `reasonRows`
  | `reasonFilter`
  | `setReasonFilter`
  | `visibleUnbookedCalls`
  | `unbookedListColumns`
> & { highlightedCallId?: string }

/**
 * Why new patient calls didn't book (plan D39): the agent's `no_booking_reason` as a
 * bar list of counts and shares, and the unbooked calls with a Reason dropdown filter.
 */
export function NoBookingReasonsSection({
  unbookedCallCount,
  reasonRows,
  reasonFilter,
  setReasonFilter,
  visibleUnbookedCalls,
  unbookedListColumns,
  highlightedCallId,
}: NoBookingReasonsSectionProps): ReactElement {
  const maxShare = Math.max(0, ...(reasonRows ?? []).map((row) => row.share))

  return (
    <div className="mb-8">
      <div className="mb-1">
        <SectionTitle
          title="No Booking Reasons"
          badge={`${unbookedCallCount.toLocaleString(`en-US`)} Unbooked Calls`}
        />
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        Why new patient calls didn&apos;t book, from the agent&apos;s no_booking_reason.
      </p>
      <div className="rounded-lg border border-default p-4">
        {!reasonRows ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner size={16} />
          </div>
        ) : !reasonRows.length ? (
          <p className="py-6 text-center text-body-sm text-subtle">No unbooked new patient calls in this date range.</p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {reasonRows.map((row) => (
              <div
                key={row.reason}
                className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_3rem_3rem] items-center gap-3 px-2 py-1.5 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_4rem_3.5rem]"
              >
                <span className="truncate text-ui-xs text-strong" title={row.label}>
                  {row.label}
                </span>
                <span className="h-2.5 overflow-hidden rounded-full bg-surface-muted">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${maxShare ? (row.share / maxShare) * 100 : 0}%`,
                      backgroundColor: REASON_BAR_COLOR,
                    }}
                  />
                </span>
                <span className="text-right text-ui-xs text-strong">{row.count.toLocaleString(`en-US`)}</span>
                <span className="text-right text-body-xs text-subtle">{`${row.share.toFixed(0)}%`}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-3 mt-6 flex flex-wrap items-center gap-3">
        <SectionTitle title="Unbooked Calls" badge={`${visibleUnbookedCalls?.length ?? 0} Calls`} />
        <Select value={reasonFilter} onValueChange={setReasonFilter}>
          <SelectTrigger
            className="h-8 w-auto min-w-[10rem] rounded-[6px] border-(--fg-strong)/10 bg-surface text-[12px] font-medium leading-[15px] text-default shadow-none hover:border-(--fg-strong)/16 hover:bg-(--fg-strong)/2 hover:text-strong"
            aria-label="Reason"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reasons</SelectItem>
            {reasonRows?.map((row) => (
              <SelectItem key={row.reason} value={row.reason}>
                {`${row.label} (${row.count})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <SimpleTable
        activeSubpage="voice-agent-ops-unbooked-calls"
        data={visibleUnbookedCalls}
        columns={unbookedListColumns}
        enableDeepLinking={false}
        hideFooter
        meta={{ getRowStyle: (row) => (row?.id === highlightedCallId ? `table-selected-row` : ``) }}
        emptyState={
          <TableEmptyState
            icon={<Phone className="h-6 w-6" strokeWidth={1.5} />}
            title="No unbooked calls"
            description="New patient calls that didn't book show up here with the agent's reason."
          />
        }
      />
    </div>
  )
}
